import { createSignal } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"
import { Dialog } from "@openanalyst-ai/ui/dialog"
import { Icon } from "@openanalyst-ai/ui/icon"
import { useDialog } from "@openanalyst-ai/ui/context/dialog"
import { useLanguage } from "@/context/language"
import { StarterPanel } from "@/components/starter-panel"
import { WorkspaceSelector } from "@/components/workspace-selector"
import { defaultProjectTemplateStarterPanel, type ProjectStarterTemplateID } from "@/services/project-starters"

export function ProjectStartChooser(props: {
  // Onboarding-v3 omits this so the "Custom project" escape hatch is
  // hidden during the guided flow — the user must pick a starter template.
  // All other callers (sidebar "New project", etc.) keep passing it.
  onCustomProject?: () => Promise<void> | void
  onStarterProject: (templateId: ProjectStarterTemplateID) => Promise<void> | void
  starterProjectRoot?: string
  onChangeStarterProjectRoot?: () => Promise<void> | void
  initialView?: "choice" | "suggested"
}) {
  const language = useLanguage()
  const [suggested, setSuggested] = createSignal(props.initialView === "suggested")
  const starterPanel = defaultProjectTemplateStarterPanel()
  const starterItems = starterPanel.sections.flatMap((section) => section.items)
  const showBack = () => props.initialView !== "suggested"

  return (
    <div data-project-start-chooser="" class="w-full">
      {suggested() ? (
        <div data-project-start-view="suggested" class="flex flex-col gap-3">
          {showBack() ? (
            <div data-project-start-back-row="">
              <Button variant="ghost" icon="arrow-left" class="w-fit px-2" onClick={() => setSuggested(false)}>
                {language.t("project.start.back")}
              </Button>
            </div>
          ) : null}
          <div data-project-start-header="">
            <div class="min-w-0">
              <div data-project-start-kicker="">{language.t("project.start.kicker")}</div>
              <div data-project-start-title="">{language.t("project.start.templates.title")}</div>
              <div data-project-start-description="">{language.t("project.start.templates.description")}</div>
            </div>
            <div data-project-start-header-actions="">
              <div data-project-start-count="">
                {language.t("project.start.suggested.badge", { count: String(starterItems.length) })}
              </div>
              {props.onCustomProject ? (
                <Button
                  variant="secondary"
                  size="small"
                  icon="folder-add-left"
                  class="shrink-0"
                  onClick={() => void props.onCustomProject?.()}
                >
                  {language.t("project.start.custom.title")}
                </Button>
              ) : null}
            </div>
          </div>
          {props.starterProjectRoot || props.onChangeStarterProjectRoot ? (
            <div data-project-start-root="">
              <div data-project-start-root-icon="">
                <Icon name="folder" size="small" />
              </div>
              <div class="min-w-0">
                <div data-project-start-root-label="">{language.t("project.start.root.label")}</div>
                <div data-project-start-root-path="" title={props.starterProjectRoot}>
                  {props.starterProjectRoot || language.t("project.start.root.default")}
                </div>
              </div>
              {props.onChangeStarterProjectRoot ? (
                <Button
                  variant="secondary"
                  size="small"
                  class="shrink-0"
                  onClick={() => void props.onChangeStarterProjectRoot?.()}
                >
                  {language.t("project.start.root.change")}
                </Button>
              ) : null}
            </div>
          ) : null}
          <StarterPanel
            panel={starterPanel}
            onAction={(item) => {
              if (item.action.kind !== "createProject") return
              void props.onStarterProject(item.action.templateId)
            }}
          />
        </div>
      ) : (
        <div data-project-start-view="choice">
          <div data-project-start-header="">
            <div class="min-w-0">
              <div data-project-start-kicker="">{language.t("project.start.kicker")}</div>
              <div data-project-start-title="">{language.t("project.start.choice.title")}</div>
              <div data-project-start-description="">{language.t("project.start.choice.description")}</div>
            </div>
          </div>
          <div data-project-start-choice-grid="">
            <button
              type="button"
              data-project-start-choice-card="suggested"
              class="group"
              onClick={() => setSuggested(true)}
            >
              <div data-project-start-choice-card-top="">
                <div data-project-start-choice-icon="">
                  <Icon name="sparkles" size="small" />
                </div>
                <div data-project-start-choice-badge="">
                  {language.t("project.start.suggested.badge", { count: String(starterItems.length) })}
                </div>
              </div>
              <div class="min-w-0">
                <div data-project-start-choice-title="">{language.t("project.start.suggested.title")}</div>
                <div data-project-start-choice-description="">{language.t("project.start.suggested.description")}</div>
              </div>
              <div data-project-start-preview="" aria-hidden="true">
                {starterItems.slice(0, 4).map((item) => (
                  <span>{item.title}</span>
                ))}
              </div>
              <div data-project-start-card-action="">
                {language.t("project.start.suggested.action")}
                <Icon name="arrow-right" size="small" />
              </div>
            </button>
            <button
              type="button"
              data-project-start-choice-card="custom"
              class="group"
              onClick={() => void props.onCustomProject?.()}
            >
              <div data-project-start-choice-card-top="">
                <div data-project-start-choice-icon="">
                  <Icon name="folder-add-left" size="small" />
                </div>
                <div data-project-start-choice-badge="">{language.t("project.start.custom.badge")}</div>
              </div>
              <div class="min-w-0">
                <div data-project-start-choice-title="">{language.t("project.start.custom.title")}</div>
                <div data-project-start-choice-description="">{language.t("project.start.custom.description")}</div>
              </div>
              <div data-project-start-card-action="">
                {language.t("project.start.custom.action")}
                <Icon name="arrow-right" size="small" />
              </div>
            </button>
          </div>
          {props.starterProjectRoot || props.onChangeStarterProjectRoot ? (
            <div data-project-start-root="choice">
              <div data-project-start-root-icon="">
                <Icon name="folder" size="small" />
              </div>
              <div class="min-w-0">
                <div data-project-start-root-label="">{language.t("project.start.root.label")}</div>
                <div data-project-start-root-path="" title={props.starterProjectRoot}>
                  {props.starterProjectRoot || language.t("project.start.root.default")}
                </div>
              </div>
              {props.onChangeStarterProjectRoot ? (
                <Button
                  variant="secondary"
                  size="small"
                  class="shrink-0"
                  onClick={() => void props.onChangeStarterProjectRoot?.()}
                >
                  {language.t("project.start.root.change")}
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  )
}

export function DialogProjectStart(props: {
  onCustomProject: () => Promise<void> | void
  onStarterProject: (templateId: ProjectStarterTemplateID) => Promise<void> | void
  starterProjectRoot?: string
  onChangeStarterProjectRoot?: () => Promise<void> | void
}) {
  const dialog = useDialog()
  const language = useLanguage()
  const afterClose = (run: () => Promise<void> | void) => {
    dialog.close()
    queueMicrotask(() => void run())
  }

  // Header is just a single Back button on the left. We use the Dialog's
  // `title` slot for the back control so it sits in the top-left of the
  // header strip, then pass an empty `action` to suppress the Dialog's
  // default close X (only the window's title-bar X remains).
  const back = (
    <Button
      variant="ghost"
      size="small"
      icon="arrow-left"
      onClick={() => dialog.close()}
      class="-ml-2"
    >
      {language.t("project.start.back")}
    </Button>
  )

  return (
    <Dialog
      title={back}
      action={<span />}
      size="large"
      class="oa-project-start-dialog"
    >
      <WorkspaceSelector
        onCustomProject={() => afterClose(props.onCustomProject)}
        onStarterProject={(templateId) => afterClose(() => props.onStarterProject(templateId))}
        starterProjectRoot={props.starterProjectRoot}
        onChangeStarterProjectRoot={props.onChangeStarterProjectRoot}
      />
    </Dialog>
  )
}
